<template>
    <div class="maintenance-admin-container">
        <h3 class="page-title">
            Maintenance Mode Administration
        </h3>
        <p class="page-description">
            Configure maintenance settings that determine whether users can access the Financial Systems Request Portal.
        </p>
        <div class="separator"></div>
        <b-form @submit.prevent="saveMaintenanceStatus">
            <b-form-group
                label="IS UNDER MAINTENANCE?"
                label-class="field-label">
                <b-form-radio-group
                    v-model="form.IsEnabled"
                    :options="yesNoOptions">
                </b-form-radio-group>
            </b-form-group>
            <b-form-group
                v-if="form.IsEnabled"
                label="IS PLANNED MAINTENANCE?"
                label-class="field-label">
                <b-form-radio-group
                    v-model="form.IsPlanned"
                    :options="yesNoOptions">
                </b-form-radio-group>
            </b-form-group>
            <b-form-group
                v-if="form.IsEnabled && form.IsPlanned"
                label="EXPECTED RESOLUTION TIME"
                label-class="field-label">
                <div class="small-control">
                   <b-form-input type="datetime-local" v-model="formattedPlannedEndTime" :min="minimumDateTime">
                    </b-form-input>
                </div>
            </b-form-group>
            <b-form-group
                v-if="form.IsEnabled"
                label="NOTIFICATION MESSAGE"
                label-class="field-label">
                <div class="small-control">
                    <b-form-textarea
                        v-model="form.NotificationMessage"
                        rows="3"
                        max-rows="4"
                        placeholder="Enter custom maintenance message...">
                    </b-form-textarea>
                </div>
                <small class="helper-text">
                    Optional. If provided, this message will override the default maintenance message displayed to users.
                </small>
            </b-form-group>         
        </b-form>
        <div class="separator bottom"></div>
       <div class="maintenance-footer">
            <b-button type="button" variant="primary" :disabled="isSaving" @click="saveMaintenanceStatus">

                {{ isSaving ? 'Saving...' : 'Save Changes' }}
            </b-button>
        </div>
        </div>
</template>
<script>
import maintenanceApi from '@/api/maintenance';
const PLANNED_MESSAGE =
    "The Financial Systems Request Portal will undergo scheduled maintenance to improve its performance and reliability. During this time, the website will be temporarily unavailable. We sincerely apologize for any inconvenience and appreciate your patience and understanding.";
const UNPLANNED_MESSAGE =
    "The Financial Systems Request Portal is currently under maintenance due to unexpected issues. During this time, the website will be temporarily unavailable. We sincerely apologize for any inconvenience and appreciate your patience and understanding.";
export default {
    name: "MaintenanceAdmin",
    props: {
        activeTab: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            form: {
                ID: 0,
                IsEnabled: false,
                IsPlanned: false,
                PlannedEndTime: null,
                NotificationMessage: ''
            },
            yesNoOptions: [
                {
                    text: 'Yes',
                    value: true
                },
                {
                    text: 'No',
                    value: false
                }
            ],
            isSaving: false,
        };
    },
    computed: {
        minimumDateTime() {

            const now = new Date();

            now.setSeconds(0, 0);
            now.setMinutes(now.getMinutes() + 1);

            const pad = (n) => String(n).padStart(2, '0');

            return (
                now.getFullYear() +
                '-' +
                pad(now.getMonth() + 1) +
                '-' +
                pad(now.getDate()) +
                'T' +
                pad(now.getHours()) +
                ':' +
                pad(now.getMinutes())
            );
        },

        formattedPlannedEndTime: {
            get() {

                if (!this.form.PlannedEndTime) {
                    return '';
                }

                const dt =
                    new Date(this.form.PlannedEndTime);

                const pad =
                    (n) => String(n).padStart(2, '0');

                return (
                    dt.getFullYear() +
                    '-' +
                    pad(dt.getMonth() + 1) +
                    '-' +
                    pad(dt.getDate()) +
                    'T' +
                    pad(dt.getHours()) +
                    ':' +
                    pad(dt.getMinutes())
                );
            },

            set(val) {

                this.form.PlannedEndTime =
                    val
                        ? new Date(val).toISOString()
                        : null;
            }
        }
    },
    watch: {

        activeTab(val) {

            if (val === 'Maintenance') {

                this.loadMaintenanceStatus();

            }
        },

        'form.IsEnabled'(value) {

            this.form.PlannedEndTime = null;

            if (value) {

                this.applyDefaultNotificationMessage();

            }
        },
        
        'form.IsPlanned'() {

            if (this.form.IsEnabled) {

                this.applyDefaultNotificationMessage();

            }
        }
    },
    created() {
        this.loadMaintenanceStatus();
    },
    methods: {
        applyDefaultNotificationMessage() {
            this.form.NotificationMessage =
                this.form.IsPlanned
                    ? PLANNED_MESSAGE
                    : UNPLANNED_MESSAGE;
        },
        async loadMaintenanceStatus() {
            try {
                const response = await maintenanceApi.getStatus();
                if (response.data) {
                    this.form.ID =
                        response.data.ID || 0;
                    this.form.IsEnabled =
                        response.data.IsEnabled || false;
                    this.form.IsPlanned =
                        response.data.IsPlanned || false;
                    this.form.PlannedEndTime =
                        response.data.PlannedEndTime || null;
                    this.form.NotificationMessage =
                        response.data.NotificationMessage || '';
                    if (!this.form.NotificationMessage) {
                        this.applyDefaultNotificationMessage();
                    }
                }
            } catch (e) {
                this.$store.dispatch(
                    "progress/setErrorMessage",
                    "Failed to load maintenance settings."
                );
            }
        },
        async saveMaintenanceStatus() {
            if (this.form.IsEnabled && this.form.IsPlanned && this.form.PlannedEndTime) {
                const selectedTime = new Date(this.form.PlannedEndTime);
                const minAllowedTime = new Date();

                minAllowedTime.setSeconds(0, 0);
                minAllowedTime.setMinutes(minAllowedTime.getMinutes() + 1);

                if (selectedTime < minAllowedTime) {
                    this.$store.dispatch(
                        "progress/setErrorMessage",
                        "Expected Resolution Time cannot be earlier than the current time."
                    );
                    return;
                }
            }

            this.isSaving = true;
            try {
                if (!this.form.IsEnabled) {
                    this.form.IsPlanned = false;
                    this.form.PlannedEndTime = null;
                    this.form.NotificationMessage = '';
                }
                if (!this.form.IsPlanned) {
                    this.form.PlannedEndTime = null;
                }
                await maintenanceApi.updateStatus(this.form);
                window.dispatchEvent(
                    new CustomEvent("maintenance-status-changed")
                );
                this.$store.dispatch(
                    "progress/setSuccessMessage",
                    this.form.IsEnabled
                        ? "Maintenance Mode has been enabled successfully."
                        : "Maintenance Mode has been disabled successfully."
                );
                setTimeout(async () => {
                    await this.loadMaintenanceStatus();
                }, 2000);
            } catch (e) {
                this.$store.dispatch(
                    "progress/setErrorMessage",
                    "Failed to update maintenance settings."
                );
            }
            finally {
                this.isSaving = false;
            }
        }
    }
};
</script>
<style scoped>
.maintenance-admin-container {
    max-width: 700px;
    margin: 20px auto;
    padding: 10px 20px;
}
.page-title {
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    color: #24364b;
    margin-bottom: 10px;
}
.page-description {
    text-align: center;
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 20px;
}
.separator {
    border-top: 1px solid #d9d9d9;
    margin-bottom: 20px;
}
.separator.bottom {
    margin-top: 15px;
    margin-bottom: 0;
}
.field-label {
    font-size: 11px;
    font-weight: 700;
    color: #24364b;
}
.form-group {
    margin-bottom: 15px;
}
.small-control {
    max-width: 650px;
}
.small-control textarea {
    min-height: 120px;
}
.maintenance-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 25px;
    padding-top: 20px;
}

.maintenance-footer .btn {
    min-width: 130px;
    font-size: 12px;
    font-weight: 600;
}
.helper-text {
    display: block;
    font-size: 11px;
    color: #6c757d;
    margin-top: 5px;
}
</style>